import {
  Home,
  Users,
  GraduationCap,
  BookOpen,
  ClipboardList,
  Wallet,
  Banknote,
  Megaphone,
  Settings,
  Eye,
  CalendarDays,
  HeartHandshake,
  MessagesSquare,
  FileText,
  TrendingUp,
  Library,
  ReceiptText,
  FileCheck,
  ToggleRight,
  School,
  Cog,
  UserCog,
  ClipboardCheck,
  Gamepad2,
  Star,
  Briefcase,
  Gauge,
  LineChart,
  Activity,
  CircleUserRound,
  CalendarOff,
  CalendarRange,
  ListChecks,
  Clock,
  ShieldCheck,
  Trophy,
  Coins,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { TranslationKey } from '../lib/i18n/en';
import type { Role } from '../lib/api';

export interface NavItem {
  to: string;
  label: string;
  /**
   * Translation key for parent-facing items.
   *
   * Present only where a parent sees the item. The sidebar renders
   * `labelKey ? t(labelKey) : label`, so staff items keep their plain English
   * label with no change and no key to maintain — the admin side stays English
   * by the owner's decision.
   *
   * `label` is kept alongside it as the English source, so the sidebar still
   * reads sensibly in a diff and nothing depends on a lookup to know what an
   * item is.
   */
  labelKey?: TranslationKey;
  icon: LucideIcon;
  /**
   * When true, the item is rendered as the role's landing page — visually
   * gold-tinted in the sidebar. There can be exactly one landing per role.
   */
  isLanding?: boolean;
  /**
   * The India day ("2026-10-17") this link's feature or change went live. The
   * sidebar shows NEW for 15 days from then, or until 7 days after the person
   * first opens it, whichever comes first (lib/new-badge.ts). When a change
   * to a page is worth noticing, set this to its go-live day; if the release
   * is held back, move the date to the day it actually goes out.
   */
  newSince?: string;
  /**
   * Optional outstanding-work count badge (e.g. "Weekly sheets · 2"). When
   * present and > 0, the sidebar shows the number next to the item and rolls
   * the value up into the parent group header. Phase 2 wires real counts;
   * Phase 1 simply renders any that are provided.
   */
  count?: number;
  /**
   * Optional query-string params merged into the destination URL, for a link
   * that lands on a pre-filtered page (e.g. `?scope=weekly_quiz`).
   */
  search?: Record<string, string>;
}

export interface NavSection {
  /**
   * Fixed id for a titled group, used to remember which group is open. It
   * never changes when the heading is renamed, so a rename doesn't reset
   * anyone's open group.
   */
  id?: string | undefined;
  title?: string | undefined;
  icon?: LucideIcon | undefined;
  items: readonly NavItem[];
}

// Canonical role-specific landing path. Exported so the index redirect, the
// shell, and the auth flow share the same mapping.
export const ROLE_LANDING_PATH: Record<Role, string> = {
  super_admin: '/control-center',
  support_viewer: '/control-center',
  academic_admin: '/operations',
  finance_admin: '/finance-ops',
  nalim: '/academic-ops',
  teacher: '/today',
  parent: '/family',
  student: '/progress',
  // Hiring portal (Module 1 / Phase 7). Candidates land on their
  // application status page; HR users reuse the academic ops landing
  // (their `hiringApps` queue is one click away in the sidebar).
  candidate: '/candidate/status',
  hr: '/academic-ops',
};

// Set of paths a role is authorised to *land* on. Used by the landing route
// guards: if a parent navigates manually to /control-center, redirect them
// home. The server enforces real RBAC; this is purely UX.
export const ROLE_LANDING_OWNERS: Record<string, ReadonlyArray<Role>> = {
  '/control-center': ['super_admin', 'support_viewer'],
  '/operations': ['academic_admin'],
  '/finance-ops': ['finance_admin'],
  '/academic-ops': ['nalim', 'hr'],
  '/today': ['teacher'],
  '/family': ['parent'],
  '/progress': ['student'],
  '/candidate/status': ['candidate'],
};

/**
 * The day the NEW badge rule and the menu tidy-up went live. Links that
 * showed NEW before, and links that moved, were renamed or merged in the
 * tidy-up, count their 15 days from here.
 */
export const MENU_TIDY_UP = '2026-10-17';

const ITEM = {
  // Landings (queue-first surfaces)
  controlCenter: { to: '/control-center', label: 'Control Center', icon: Gauge, isLanding: true },
  operations:    { to: '/operations',     label: 'Operations',     icon: ClipboardCheck, isLanding: true },
  financeOps:    { to: '/finance-ops',    label: 'Finance Ops',    icon: Wallet, isLanding: true },
  academicOps:   { to: '/academic-ops',   label: 'Academic Ops',   icon: School, isLanding: true },
  today:         { to: '/today',          label: 'Today',          icon: CalendarDays, isLanding: true },
  family:        { to: '/family',         label: 'Family',         labelKey: 'nav.family',        icon: HeartHandshake, isLanding: true },
  progress:      { to: '/progress',       label: 'My Progress',    icon: TrendingUp, isLanding: true },

  // Existing portal items
  students:   { to: '/people/students',   label: 'Students',           icon: GraduationCap },
  teachers:   { to: '/people/teachers',   label: 'Teachers',           icon: Users },
  managers:   { to: '/people/managers',   label: 'Nalims',             icon: Users, newSince: MENU_TIDY_UP },
  households: { to: '/people/households', label: 'Families',           icon: HeartHandshake, newSince: MENU_TIDY_UP },
  batches:    { to: '/academics/batches', label: 'Batches',            icon: CalendarDays },
  attendance: { to: '/academics/attendance', label: 'Attendance',      icon: ClipboardList },
  weekly:     { to: '/academics/weekly-sheet', label: 'Weekly sheets', icon: BookOpen },
  meetings:   { to: '/academics/meetings', label: 'Meetings',          icon: MessagesSquare },
  resources:  { to: '/academics/resources', label: 'Resources',        icon: FileText },
  academicsProgress: { to: '/academics/progress', label: 'Student progress', icon: TrendingUp, newSince: MENU_TIDY_UP },
  courses:    { to: '/academics/courses', label: 'Courses',            icon: Library },
  fees:       { to: '/finance/fees',      label: 'Fees',               icon: Wallet },
  salaries:   { to: '/finance/salaries',  label: 'Salaries',           icon: Banknote },
  claims:     { to: '/finance/fees/claims', label: 'Payments parents reported', icon: ReceiptText, newSince: MENU_TIDY_UP },
  receipts:   { to: '/finance/receipts',  label: 'Receipts & payslips', icon: FileCheck },
  announce:   { to: '/announcements',     label: 'Announcements',      labelKey: 'nav.announcements',   icon: Megaphone },
  users:      { to: '/system/users',      label: 'Logins & roles',     icon: UserCog, newSince: MENU_TIDY_UP },
  audit:      { to: '/system/audit',      label: 'Activity history',   icon: Eye, newSince: MENU_TIDY_UP },
  settings:   { to: '/system/settings',   label: 'Settings',           icon: Settings },
  featureFlags: { to: '/system/feature-flags', label: 'Switch features on or off', icon: ToggleRight, newSince: MENU_TIDY_UP },
  myFees:     { to: '/me/fees',           label: 'My fees',            labelKey: 'nav.myFees',          icon: Wallet },
  myKids:     { to: '/me/children',       label: 'My children',        labelKey: 'nav.myChildren',      icon: HeartHandshake },
  myAttendance: { to: '/me/attendance',   label: 'My attendance',      labelKey: 'nav.myAttendance',    icon: ClipboardList },
  myMarks:    { to: '/me/marks',          label: 'My marks',           labelKey: 'nav.myMarks',         icon: FileCheck,    newSince: MENU_TIDY_UP },

  // New-module items (route may 404 in Phase 1 — Phase 2 fills in)
  myPerformance:   { to: '/me/performance',          label: 'My performance',  icon: LineChart,    newSince: MENU_TIDY_UP },
  parentFeedback:  { to: '/me/feedback',             label: 'Parent feedback', icon: MessagesSquare, newSince: MENU_TIDY_UP },
  referralsWallet: { to: '/me/referrals',             label: 'Referrals & wallet', labelKey: 'nav.referralsWallet', icon: Wallet,    newSince: MENU_TIDY_UP },
  teacherFeedback: { to: '/me/feedback',             label: 'Teacher feedback', labelKey: 'nav.teacherFeedback', icon: MessagesSquare, newSince: MENU_TIDY_UP },
  games:           { to: '/games',                   label: 'Games',           icon: Gamepad2,     newSince: MENU_TIDY_UP },
  hiringApps:      { to: '/hr/applications',         label: 'Applications',    icon: Briefcase,    newSince: MENU_TIDY_UP },
  hiringInter:     { to: '/hr/interviews',           label: 'Interviews',      icon: Briefcase,    newSince: MENU_TIDY_UP },
  hiringProbation: { to: '/hr/probation',            label: 'Probation',       icon: Briefcase,    newSince: MENU_TIDY_UP },
  exams:           { to: '/academics/exams',         label: 'Exams & marks',   icon: FileCheck,    newSince: MENU_TIDY_UP },
  assessments:     { to: '/academics/assessments',   label: 'Tests & quizzes', icon: ClipboardCheck, newSince: MENU_TIDY_UP },
  myAssessments:   { to: '/me/assessments',          label: 'My tests & quizzes', icon: ClipboardCheck, newSince: MENU_TIDY_UP },
  tma:             { to: '/hr/performance',          label: 'Performance scores', icon: Star, newSince: MENU_TIDY_UP },
  feedbackInsight: { to: '/hr/feedback',              label: 'Parent feedback',icon: MessagesSquare, newSince: MENU_TIDY_UP },
  withdrawals:     { to: '/finance/withdrawals',     label: 'Withdrawals',     icon: Wallet,       newSince: MENU_TIDY_UP },
  perfConfig:      { to: '/system/performance-config', label: 'Score settings', icon: Activity, newSince: MENU_TIDY_UP },
  hiringConfig:    { to: '/system/hiring-config',    label: 'Hiring settings', icon: Briefcase,    newSince: MENU_TIDY_UP },
  walletAdjust:    { to: '/system/wallet/adjustments', label: 'Referral balances', icon: Wallet,    newSince: MENU_TIDY_UP },
  // Academy calendar (holidays, class exceptions) and teacher leave.
  calendar:        { to: '/system/calendar',         label: 'Academy calendar', icon: CalendarRange, newSince: MENU_TIDY_UP },
  leave:           { to: '/academics/leave',         label: 'Teacher leave',   icon: CalendarOff,  newSince: MENU_TIDY_UP },
  myLeave:         { to: '/me/leave',                label: 'My leave',        icon: CalendarOff,  newSince: MENU_TIDY_UP },
  // Teacher reports (module_teacher_reports).
  myReports:       { to: '/me/reports',              label: 'My reports',      icon: ListChecks,   newSince: MENU_TIDY_UP },
  reportSettings:  { to: '/system/teacher-reports',  label: 'Report settings', icon: ListChecks,   newSince: MENU_TIDY_UP },
  myPoints:        { to: '/me/points',               label: 'My points',       icon: Coins,        newSince: MENU_TIDY_UP },
  reportReview:    { to: '/academics/report-review', label: 'Reports to review', icon: ShieldCheck, newSince: MENU_TIDY_UP },
  reportBoard:     { to: '/academics/report-leaderboard', label: 'Leaderboard', icon: Trophy,      newSince: MENU_TIDY_UP },
  incentives:      { to: '/finance/incentives',      label: 'Report incentives', icon: Coins, newSince: MENU_TIDY_UP },
  scheduledJobs:   { to: '/system/scheduled-jobs',   label: 'Scheduled jobs', icon: Clock,       newSince: MENU_TIDY_UP },

  // Candidate self-service items (Module 1 / Phase 7).
  candidateStatus:    { to: '/candidate/status',          label: 'My application',  icon: Briefcase,    isLanding: true },
  candidateAptitude:  { to: '/candidate/aptitude',        label: 'Aptitude test',   icon: ClipboardCheck },
  candidateInterview: { to: '/candidate/interview/book',  label: 'Interview',       icon: CalendarDays },
  candidateDemos:     { to: '/candidate/demos',           label: 'Demo classes',    icon: Star },
  candidateRules:     { to: '/candidate/rules',           label: 'Rules & test',    icon: FileText },
  candidateOnboard:   { to: '/candidate/onboard',         label: 'Onboarding',      icon: HeartHandshake },
} as const satisfies Record<string, NavItem>;

/** A link that moved to another group for this role only: NEW from the tidy-up. */
const moved = (item: NavItem): NavItem => ({ ...item, newSince: MENU_TIDY_UP });

export const navForRole = (role: Role): readonly NavSection[] => {
  switch (role) {
    case 'super_admin':
      return [
        { items: [ITEM.controlCenter] },
        { id: 'people', title: 'People', icon: Users, items: [ITEM.students, ITEM.households, ITEM.teachers, ITEM.managers] },
        { id: 'classes', title: 'Classes', icon: School, items: [ITEM.batches, ITEM.courses, ITEM.attendance, ITEM.weekly, ITEM.academicsProgress, ITEM.exams, ITEM.assessments, ITEM.resources, ITEM.meetings] },
        { id: 'calendar-leave', title: 'Calendar & leave', icon: CalendarRange, items: [ITEM.calendar, ITEM.leave] },
        { id: 'teacher-reports', title: 'Teacher reports', icon: ListChecks, items: [ITEM.reportReview, ITEM.reportBoard, ITEM.reportSettings] },
        { id: 'teacher-performance', title: 'Teacher performance', icon: LineChart, items: [ITEM.tma, ITEM.feedbackInsight, ITEM.perfConfig] },
        { id: 'fees-pay', title: 'Fees & pay', icon: Wallet, items: [ITEM.fees, ITEM.claims, ITEM.salaries, ITEM.incentives, ITEM.receipts, ITEM.withdrawals, ITEM.walletAdjust] },
        { id: 'hiring', title: 'Hiring', icon: Briefcase, items: [ITEM.hiringApps, ITEM.hiringInter, ITEM.hiringProbation, ITEM.hiringConfig] },
        { items: [ITEM.announce] },
        { id: 'system', title: 'System', icon: Cog, items: [ITEM.users, ITEM.settings, ITEM.featureFlags, ITEM.scheduledJobs, ITEM.audit] },
      ];
    case 'academic_admin':
      return [
        { items: [ITEM.operations] },
        { id: 'people', title: 'People', icon: Users, items: [ITEM.students, ITEM.households, ITEM.teachers, ITEM.managers] },
        { id: 'classes', title: 'Classes', icon: School, items: [ITEM.batches, ITEM.courses, ITEM.attendance, ITEM.weekly, ITEM.academicsProgress, ITEM.exams, ITEM.assessments, ITEM.resources, ITEM.meetings] },
        { id: 'calendar-leave', title: 'Calendar & leave', icon: CalendarRange, items: [ITEM.calendar, ITEM.leave] },
        { id: 'teacher-reports', title: 'Teacher reports', icon: ListChecks, items: [ITEM.reportReview, ITEM.reportBoard, ITEM.reportSettings] },
        { id: 'teacher-performance', title: 'Teacher performance', icon: LineChart, items: [ITEM.tma, ITEM.feedbackInsight] },
        { id: 'hiring', title: 'Hiring', icon: Briefcase, items: [ITEM.hiringApps, ITEM.hiringInter, ITEM.hiringProbation] },
        { items: [ITEM.announce] },
      ];
    case 'finance_admin':
      return [
        { items: [ITEM.financeOps] },
        { id: 'fees-pay', title: 'Fees & pay', icon: Wallet, items: [ITEM.fees, ITEM.claims, ITEM.salaries, ITEM.incentives, ITEM.receipts, ITEM.withdrawals] },
        { id: 'people', title: 'People', icon: Users, items: [ITEM.households, ITEM.students] },
        { items: [ITEM.announce] },
        { id: 'system', title: 'System', icon: Cog, items: [ITEM.settings, ITEM.audit] },
      ];
    case 'nalim':
      return [
        { items: [ITEM.academicOps] },
        { id: 'classes', title: 'Classes', icon: School, items: [ITEM.batches, ITEM.courses, ITEM.attendance, ITEM.weekly, ITEM.academicsProgress, ITEM.exams, ITEM.assessments, ITEM.resources, ITEM.meetings] },
        { id: 'my-teachers', title: 'My teachers', icon: Users, items: [moved(ITEM.teachers), ITEM.leave, ITEM.reportReview, ITEM.reportBoard, ITEM.tma] },
        { items: [moved(ITEM.students), ITEM.announce] },
      ];
    case 'teacher':
      return [
        { items: [ITEM.today] },
        { id: 'my-classes', title: 'My classes', icon: School, items: [moved(ITEM.attendance), moved(ITEM.weekly), ITEM.exams, ITEM.assessments, ITEM.academicsProgress, moved(ITEM.resources)] },
        { id: 'reporting', title: 'Reporting', icon: ListChecks, items: [ITEM.myReports, ITEM.myPoints] },
        { id: 'about-me', title: 'About me', icon: CircleUserRound, items: [ITEM.myPerformance, ITEM.parentFeedback, ITEM.myLeave, ITEM.referralsWallet] },
        { items: [ITEM.meetings, ITEM.announce] },
      ];
    case 'parent':
      return [
        { items: [ITEM.family, ITEM.myKids, ITEM.myFees, ITEM.myAttendance, ITEM.myMarks, ITEM.teacherFeedback, ITEM.referralsWallet, ITEM.announce] },
      ];
    case 'student':
      return [
        { items: [ITEM.progress, ITEM.myAttendance, ITEM.myMarks, ITEM.myAssessments, ITEM.games, ITEM.referralsWallet, ITEM.announce] },
      ];
    case 'support_viewer':
      return [
        { items: [ITEM.controlCenter] },
        { id: 'people', title: 'People', icon: Users, items: [ITEM.students, ITEM.teachers, ITEM.users] },
        { id: 'system', title: 'System', icon: Cog, items: [ITEM.audit, ITEM.scheduledJobs] },
      ];
    case 'candidate':
      return [
        { items: [ITEM.candidateStatus] },
        {
          items: [
            ITEM.candidateAptitude,
            ITEM.candidateInterview,
            ITEM.candidateDemos,
            ITEM.candidateRules,
            ITEM.candidateOnboard,
          ],
        },
      ];
    case 'hr':
      return [
        { items: [ITEM.academicOps] },
        { id: 'hiring', title: 'Hiring', icon: Briefcase, items: [ITEM.hiringApps, ITEM.hiringInter, ITEM.hiringProbation, ITEM.hiringConfig] },
        { items: [ITEM.assessments, ITEM.announce] },
      ];
    default:
      return [{ items: [{ to: '/', label: 'Home', icon: Home }] }];
  }
};

/**
 * Where a page sits in this role's menu, as people read it: "System → Settings"
 * for a link in a group, just the link's name for one on its own, or `null`
 * when the role has no link to it. On-screen directions are built with this,
 * so they're right for everyone who sees them and follow any menu change.
 */
export const menuPath = (role: Role, to: string): string | null => {
  for (const section of navForRole(role)) {
    const item = section.items.find((i) => i.to === to && !i.search);
    if (!item) continue;
    return section.title ? `${section.title} → ${item.label}` : item.label;
  }
  return null;
};

/** Lucide icon for the candidate placeholder route. */
export { CircleUserRound as CandidateIcon };
